import { useState, useEffect } from "react";
import { getBondLadder } from"../../http/api";
import { useAuth } from "../../Context/AuthProvider";
import { withUiFilterKeyAliases } from "../../Utils/filterKeyAliases";

// Helper function to ensure we never have more than the specified number of items
const limitToMaxItems = (array, maxItems) => {
  return array.length > maxItems ? array.slice(0, maxItems) : array;
};

// Helper to check if an item with the same ID already exists in the array
const isLadderAlreadyInArray = (array, newLadder) => {
  return array.some(item => item.bondLadderId === newLadder.bondLadderId);
};

// Process incoming ladder data to ensure it has the right format
const processLadderData = (ladder) => {
  // If the ladder has characteristics as a string, parse it
  if (ladder.characteristics && typeof ladder.characteristics === 'string') {
    try {
      // Stored under the backend spelling (minCoupon, ...), read under the UI spelling
      const parsedCharacteristics = withUiFilterKeyAliases(
        JSON.parse(ladder.characteristics)
      );
      return {
        ...ladder,
        parsedCharacteristics
      };
    } catch (e) {
      console.error("Error parsing ladder characteristics:", e);
    }
  }
  
  return ladder;
};

export const useBondLadders = (token, page = 0, size = 20, columnToSort = "bondLadderId", sortingDirection = "desc", visitedTabs = new Set(), setVisitedTabs = () => {}, tabId = "0-2") => {
  const [bondLadderData, setBondLadderData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const { role } = useAuth();

  useEffect(() => {
    // Skip API call if token is null (meaning this tab is not active)
    if (!token) return;

    // Check if we've already visited this tab and page combination
    // If we have, don't make the API call again
    const tabPageKey = `${tabId}-${page}-${size}`;
    if (visitedTabs.has(tabPageKey)) {
      return;
    }

    const fetchBondLadders = async () => {
      setLoading(true);
      try {
        const response = await getBondLadder(token, page, size, columnToSort, sortingDirection);
        setBondLadderData(response.content || []);
        setTotalElements(response.totalElements || 0);
        
        // Mark this tab-page combination as visited by replacing any existing entries for this tab
        setVisitedTabs(prev => {
          const newSet = new Set(prev);
          // Remove any previous entries for this tab (regardless of page)
          Array.from(prev).forEach(key => {
            if (key.startsWith(`${tabId}-`)) {
              newSet.delete(key);
            }
          });
          // Add the current tab-page combination
          newSet.add(tabPageKey);
          return newSet;
        });
      } catch (error) {
        console.error("Error fetching bond ladder:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBondLadders();
  }, [token, page, size, columnToSort, sortingDirection, tabId, visitedTabs, setVisitedTabs]);

  // Add WebSocket event listeners for ladder updates
  useEffect(() => {
    const handleLadderUpdate = (event) => {
    
      
      // Handle both single ladder and array of ladders
      const ladderData = Array.isArray(event.detail) ? event.detail : [event.detail];
      
      if (ladderData.length === 0) return;
      
      // Process each ladder and update state
      setBondLadderData(prevData => {
        // Make a fresh copy of the previous data
        const updatedData = [...prevData];
        
        // Process each ladder in the update
        ladderData.forEach(ladder => {
          const processedLadder = processLadderData(ladder);
          
          // Find if this ladder already exists in our data
          const existingIndex = updatedData.findIndex(
            item => item.bondLadderId === processedLadder.bondLadderId
          );
          
          if (existingIndex >= 0) {
            // Update existing ladder
            updatedData[existingIndex] = {
              ...updatedData[existingIndex],
              ...processedLadder
            };
          } else if (page === 0) {
            // Only add new ladders when on the first page
            // Add to the beginning to show newest first
            updatedData.unshift(processedLadder);
          }
        });
        
        // Ensure we don't exceed the page size
        return limitToMaxItems(updatedData, size);
      });
      
      // If we're on the first page, we might need to update total count
      if (page === 0) {
        setTotalElements(prevTotal => {
          // For new items that don't already exist in our data
          const newItemsCount = ladderData.filter(
            ladder => !bondLadderData.some(item => item.bondLadderId === ladder.bondLadderId)
          ).length;
          
          return prevTotal + newItemsCount;
        });
      }
    };
    
    // Determine which event to listen to based on user role
    const eventName ="ladderUpdate";
    // Add event listeners
    window.addEventListener(eventName, handleLadderUpdate);
    
    // Cleanup
    return () => {
      window.removeEventListener(eventName, handleLadderUpdate);
    };
  }, [bondLadderData, page, size, role]);

  return { bondLadderData, setBondLadderData, totalElements, loading };
};